/**
 * Intake Service
 * AIDLC Administrative Backend
 *
 * Reads the requirements intakes the CRM sent for a project (each with where
 * its requirements stand) and the cost roll-up: estimate against actual by
 * project, by customer, by agent within each project, and for the unit.
 */

import apiClient from './api';

export interface TokenEstimateView {
  inputTokens: number;
  outputTokens: number;
  estimatedCost: number;
  estimatedDuration: number;
  taskClass: string;
  confidence: 'low' | 'medium' | 'high';
  model: string;
  reasoning: string;
}

export interface Spend {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  totalTokens: number;
  costUsd: number;
  sessions: number;
}

export interface Estimated {
  items: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costUsd: number;
}

export interface IntakeRequirementRow {
  externalRef: string;
  ref: string;
  itemId: string;
  title: string;
  status: string;
  withdrawn: boolean;
  assignedAgent: string | null;
  /** The connectors the latest revision links to this requirement, by the CRM's reference. Absent when none. */
  connectorRefs?: string[];
  estimate: TokenEstimateView | null;
  baselineEstimate: TokenEstimateView | null;
  actual: Spend;
}

/** A system the product must exchange data with, described in words: no address and no credential. */
export interface IntakeConnector {
  externalRef: string;
  ref: string;
  system: string;
  purpose: string | null;
  direction: 'inbound' | 'outbound' | 'both';
  dataKind: string | null;
  method: string | null;
  frequency: string | null;
  authentication: string | null;
  owner: { name: string; title: string | null } | null;
  testEnvironment: 'yes' | 'no' | 'unknown';
  patientInformation: 'yes' | 'no' | 'not_sure';
  informationOnly: boolean;
  notes: string | null;
  status: 'active' | 'withdrawn';
  requirementRefs: string[];
}

/** A supporting document the CRM holds. The portal has what it is, never the file and never its file name. */
export interface IntakeDocument {
  externalRef: string;
  ref: string;
  /** Empty for a destroyed document whose title was removed with it. */
  title: string;
  description: string | null;
  type: string;
  sizeBytes: number;
  sha256: string;
  status: 'current' | 'superseded' | 'withdrawn' | 'destroyed';
  replacesRef: string | null;
  requirementRefs: string[];
  connectorRefs: string[];
}

export interface IntakePacket {
  externalRef: string;
  number: string;
  title: string;
  customerName: string;
  revision: number;
  approvedBy: string | null;
  approvedOn: string | null;
  questionnaireVersion: string | null;
  sections: Array<{ key: string; title: string; answers: Array<{ key: string; question: string; answer: string; answeredBy: string | null }> }>;
  stakeholders: Array<{ name: string; title: string | null; role: string | null }>;
  /** Absent when the latest revision sent none. */
  connectors?: IntakeConnector[];
  documents?: IntakeDocument[];
  receivedAt: string;
  updatedAt: string;
}

export interface IntakeStatus {
  intake: IntakePacket;
  project: { id: string; name: string; aidlcState: string; currentPhase: string | null };
  requirements: IntakeRequirementRow[];
  estimated: { requirements: number; inputTokens: number; outputTokens: number; totalTokens: number; costUsd: number; durationMinutes: number };
  actual: { totalTokens: number; costUsd: number; sessions: number };
  /** What a reader is told wherever the documents are listed: the server's own fixed sentence, present whenever the
   *  intake has documents. The only source of it on this side; it is never text from a packet. */
  documentNotice?: string;
}

export interface AgentSpend extends Spend { agentId: string; agentName: string }

export interface ProjectRollup {
  projectId: string;
  projectName: string;
  aidlcState: string;
  crmCustomerId: string | null;
  customerName: string | null;
  revenueUsd: number | null;
  actual: Spend;
  estimated: Estimated;
  varianceUsd: number | null;
  openItems: number;
  doneItems: number;
  byAgent: AgentSpend[];
}

export interface CustomerRollup {
  crmCustomerId: string;
  customerName: string;
  projects: number;
  revenueUsd: number | null;
  actual: Spend;
  estimated: Estimated;
  varianceUsd: number | null;
  marginUsd: number | null;
}

export interface AgentRollup extends AgentSpend {
  byProject: Array<Spend & { projectId: string | null; projectName: string }>;
}

export interface CostRollup {
  generatedAt: string;
  unit: { actual: Spend; estimated: Estimated; revenueUsd: number | null; projects: number; customers: number };
  customers: CustomerRollup[];
  internal: { projects: number; actual: Spend; estimated: Estimated };
  unattributed: Spend;
  projects: ProjectRollup[];
  agents: AgentRollup[];
}

export async function getProjectIntakes(projectId: string): Promise<IntakeStatus[]> {
  const response = await apiClient.get<IntakeStatus[]>(`/projects/${projectId}/intakes`);
  return response.data;
}

export async function getCostRollup(): Promise<CostRollup> {
  const response = await apiClient.get<CostRollup>('/tokens/rollup');
  return response.data;
}
